// src/components/complianceParams.js
// ---------------------------------------------------------------------------
// The MASTER APPROVAL PARAMETER LIST, encoded as engine-shaped definitions.
//
// Source: DE-CON Master Approval Parameter List (derived from the Vapi CGDCR-2017
// scrutiny report + the India compliance category report + DE-CON 5b).
//
// This is the SKELETON, not a rulebook: every entry has `rules: []`, so each row
// resolves to NOT ENCODED until a params/<code>/<version>.json supplies real
// values. Its job is to make the full checklist visible — an architect can see
// what will be checked, and what is still missing, instead of a short list that
// looks complete.
//
// `group` drives the sub-headings in the card (A1..A5 etc). `note` is shown as
// the row's caption when nothing is encoded yet.
// ---------------------------------------------------------------------------

const p = (id, label, opts = {}) => [id, {
  label,
  unit: opts.unit ?? '',
  check: opts.check ?? 'min',
  resolve: opts.resolve ?? 'single',
  severity: opts.severity ?? 'compounding',
  achievedSource: opts.achievedSource ?? 'massing',
  group: opts.group,
  note: opts.note,
  rules: [],
}];

const obj = (...entries) => Object.fromEntries(entries);

// --- A. EXTERIOR — deterministic vs massing envelope ------------------------
export const EXTERIOR = obj(
  // A1. Plot & site metrics
  p('plot_area_min', 'Plot area (minimum for use)', { unit: 'm²', check: 'min', severity: 'stop-work', achievedSource: 'document', group: 'Plot & site',
     note: 'Documented value (property card / approved layout) — not the drawn polygon.' }),
  p('plot_width', 'Plot width / frontage', { unit: 'm', group: 'Plot & site' }),
  p('road_width', 'Abutting road width', { unit: 'm', severity: 'stop-work', achievedSource: 'input', group: 'Plot & site',
     note: 'Drives the road-width setback and FSI bands.' }),
  p('net_plot_area', 'Net plot area (after surrender)', { unit: 'm²', achievedSource: 'document', group: 'Plot & site' }),
  p('plot_area_for_fsi', 'Plot area for FSI', { unit: 'm²', severity: 'stop-work', achievedSource: 'document', group: 'Plot & site',
     note: 'Provenance trap: the FSI basis is a documented figure, never the drawn polygon.' }),

  // A2. Coverage & FSI
  p('ground_coverage', 'Ground coverage', { unit: '%', check: 'max', severity: 'stop-work', group: 'Coverage & FSI' }),
  p('fsi', 'FSI / FAR (base + chargeable)', { check: 'max', severity: 'stop-work', group: 'Coverage & FSI',
     note: 'Tiered: base FSI plus paid/chargeable FSI — a cost lever, not only a ceiling.' }),
  p('consumed_fsi', 'Consumed FSI factor', { check: 'max', severity: 'stop-work', group: 'Coverage & FSI' }),
  p('built_up_area', 'Built-up area (BUA)', { unit: 'm²', check: 'max', group: 'Coverage & FSI' }),

  // A3. Height
  p('height', 'Building height (from ground level)', { unit: 'm', check: 'max', resolve: 'max', severity: 'stop-work', group: 'Height' }),
  p('floor_height', 'Floor-to-floor / clear height', { unit: 'm', group: 'Height' }),
  p('plinth_height', 'Plinth height', { unit: 'm', group: 'Height' }),
  p('parapet_height', 'Parapet / railing height', { unit: 'm', group: 'Height' }),

  // A4. Margins & setbacks — the Vapi core
  p('front_margin', 'Front / road-side margin', { unit: 'm', resolve: 'max', severity: 'demolition', group: 'Margins & setbacks',
     note: 'Governing value = max(road-width rule, height-driven rule).' }),
  p('side_margin', 'Side margin (each)', { unit: 'm', resolve: 'max', severity: 'demolition', group: 'Margins & setbacks' }),
  p('rear_margin', 'Rear margin', { unit: 'm', resolve: 'max', severity: 'demolition', group: 'Margins & setbacks' }),
  p('basement_margin', 'Basement margin', { unit: 'm', resolve: 'max', severity: 'stop-work', group: 'Margins & setbacks' }),
  p('highway_frontage_margin', 'Highway / special-road frontage margin', { unit: 'm', resolve: 'max', severity: 'demolition', group: 'Margins & setbacks' }),
  p('common_plot_margin', 'Margin between common plot & building', { unit: 'm', severity: 'stop-work', group: 'Margins & setbacks' }),

  // A5. Projections, roads, trees, reservations
  p('projections', 'Architectural projections into margin', { unit: 'm', check: 'max', group: 'Projections & reservations' }),
  p('internal_road_width', 'Internal / access road width', { unit: 'm', severity: 'stop-work', group: 'Projections & reservations' }),
  p('road_widening_surrender', 'Road-widening / building-line surrender', { check: 'required-present', severity: 'stop-work', achievedSource: 'document', group: 'Projections & reservations' }),
  p('flood_line_margin', 'Flood / blue-red-line margin', { unit: 'm', severity: 'demolition', group: 'Projections & reservations' }),
  p('tree_count', 'Tree count (per plot area)', { achievedSource: 'input', group: 'Projections & reservations' }),
  p('common_plot', 'Common plot — area / width / access', { unit: 'm²', severity: 'oc-denial', group: 'Projections & reservations' }),
);

// --- B. INTERIOR — vs parsed floor plan -------------------------------------
export const INTERIOR = obj(
  p('min_room_size', 'Minimum room size (per room type)', { unit: 'm²', achievedSource: 'plan', group: 'Rooms' }),
  p('room_height', 'Room / habitable height', { unit: 'm', achievedSource: 'plan', group: 'Rooms' }),
  p('kitchen_ventilation', 'Ventilation — openable area (≥1/10 floor area)', { unit: '%', severity: 'oc-denial', achievedSource: 'plan', group: 'Light & ventilation' }),
  p('window_vent', 'Window / vent opening size & location', { unit: 'm²', achievedSource: 'plan', group: 'Light & ventilation' }),
  p('wc_handicapped', 'Accessible WC (minimum size)', { unit: 'm²', severity: 'oc-denial', achievedSource: 'plan', group: 'Circulation & sanitation' }),
  p('passage_circulation', 'Attached toilet / passage / circulation', { unit: 'm', achievedSource: 'plan', group: 'Circulation & sanitation' }),
);

// --- C. AMENITIES & BUILDING SERVICES ---------------------------------------
export const AMENITIES = obj(
  p('parking_car', 'Parking — car (ratio of utilised FSI)', { severity: 'oc-denial', achievedSource: 'input', group: 'Parking' }),
  p('parking_visitor', 'Parking — visitor', { severity: 'oc-denial', achievedSource: 'input', group: 'Parking' }),
  p('parking_loading', 'Parking — loading / unloading', { achievedSource: 'input', group: 'Parking' }),
  p('parking_two_wheeler', 'Parking — two-wheeler / stack', { achievedSource: 'input', group: 'Parking' }),
  p('visitor_parking_ground', 'Visitor parking at ground (≥50%)', { achievedSource: 'input', group: 'Parking' }),

  p('staircase_count', 'Staircase count (common + fire)', { severity: 'stop-work', achievedSource: 'input', group: 'Life safety' }),
  p('staircase_width', 'Staircase width / riser / tread', { unit: 'm', severity: 'stop-work', achievedSource: 'input', group: 'Life safety' }),
  p('refuge_area', 'Refuge area (size + count, >25 m)', { unit: 'm²', severity: 'oc-denial', achievedSource: 'input', group: 'Life safety' }),
  p('fire_tender_access', 'Fire-tender access / turning radius', { unit: 'm', severity: 'oc-denial', achievedSource: 'input', group: 'Life safety' }),
  p('lift_count', 'Lift count + fire lift', { severity: 'oc-denial', achievedSource: 'input', group: 'Life safety' }),
  p('lift_dimensions', 'Lift car / lobby / door dimensions', { unit: 'm', achievedSource: 'input', group: 'Life safety' }),
  p('ramp', 'Ramp width / slope / platform', { unit: 'm', achievedSource: 'input', group: 'Life safety' }),

  p('sanitation_wc', 'Sanitation — WC / urinal (by occupancy)', { severity: 'oc-denial', achievedSource: 'input', group: 'Services' }),
  p('ventilation_shaft', 'Ventilation shaft (min 0.9 × 0.9)', { unit: 'm', group: 'Services' }),
  p('rwh', 'Rainwater harvesting', { check: 'required-present', severity: 'oc-denial', achievedSource: 'input', group: 'Water & environment' }),
  p('stp', 'Sewage Treatment Plant', { check: 'required-present', severity: 'oc-denial', achievedSource: 'input', group: 'Water & environment' }),
  p('rwst', 'Underground / rainwater storage tank', { check: 'required-present', achievedSource: 'input', group: 'Water & environment' }),
  p('percolation_well', 'Percolation well', { check: 'required-present', achievedSource: 'input', group: 'Water & environment' }),
);

export const SKELETON = {
  regulationName: 'No DCR encoded for this location — achieved values only',
  parameters: { exterior: EXTERIOR, interior: INTERIOR, amenities: AMENITIES },
};

/** Group order per category, so sub-headings render in checklist order. */
export const GROUP_ORDER = {
  exterior: ['Plot & site', 'Coverage & FSI', 'Height', 'Margins & setbacks', 'Projections & reservations'],
  interior: ['Rooms', 'Light & ventilation', 'Circulation & sanitation'],
  amenities: ['Parking', 'Life safety', 'Services', 'Water & environment'],
};

/**
 * Overlay a resolved DCR file on the skeleton so every checklist row stays
 * visible: encoded params render pass/fail, the rest render NOT ENCODED with
 * their achieved value. A partial rulebook must not make the list look short.
 */
export function mergeSkeleton(file) {
  if (!file) return SKELETON;
  const cats = ['exterior', 'interior', 'amenities'];
  const parameters = {};
  cats.forEach((c) => {
    parameters[c] = { ...SKELETON.parameters[c], ...((file.parameters || {})[c] || {}) };
    // keep the skeleton's group label when the rulebook doesn't carry one
    Object.keys(parameters[c]).forEach((id) => {
      const skel = SKELETON.parameters[c][id];
      if (skel && !parameters[c][id].group) {
        parameters[c][id] = { ...parameters[c][id], group: skel.group };
      }
    });
  });
  return { ...file, parameters };
}
